import React, { forwardRef, ReactNode } from 'react'
import momentTz, { Moment as MomentTimezone } from 'moment-timezone'
import moment, { Moment } from 'moment-timezone'
import { Box, Text } from '@chilipiper/design-system'
import { BookerCalendarEvent } from '@chilipiper/api-type-def'
import { useLocale } from '@chilipiper/locale'
import './days.css'
import './availability.css'
import { ScreenReaderOnly, UnstyledButton } from '@chilipiper/design-system'

const focusFirstAvailableTimeSlot = () => {
  setTimeout(() => {
    const firstAvailableTimeSlot = document.querySelector(
      'button.fabe-time-slot'
    ) as HTMLButtonElement
    firstAvailableTimeSlot?.focus()
  }, 5)
}
interface Props {
  availableDays?: Record<string, boolean>
  date: MomentTimezone
  days: number
  events: BookerCalendarEvent[]
  hideArrows: boolean
  noMemberFound: boolean
  onDayChange: (day: ExtendedMoment) => void
  startDate: MomentTimezone
  timezone: string
  titleNode: ReactNode
}

type ExtendedMoment = MomentTimezone & {
  empty: boolean
  isAfterEnd: boolean
  isBeforeStart: boolean
}

interface State {
  availableDays: Record<string, boolean>
  date: Moment
  daySlotWidth?: number
  days: ExtendedMoment[]
  events: BookerCalendarEvent[]
  loaded: boolean
}

class Days extends React.Component<Props, State> {
  state = {
    date: moment(),
    days: [],
    loaded: false,
    availableDays: {},
    daySlotWidth: 0,
    events: [],
  }

  daysHolder = React.createRef<HTMLDivElement>()
  dayHolder = React.createRef<HTMLDivElement>()

  animationInterval: ReturnType<typeof setInterval> | undefined = undefined

  lastDistance = 0

  animateScrollLeft = (element: HTMLElement, distance: number, duration: number) => {
    if (this.animationInterval) {
      clearInterval(this.animationInterval)
      element.scrollLeft = this.lastDistance
    }
    const interval = 10
    const step = ((distance - element.scrollLeft) / duration) * interval
    const direction = distance - element.scrollLeft > 0
    this.lastDistance = distance
    this.animationInterval = setInterval(() => {
      element.scrollLeft += step
      const endAnimation =
        (element.scrollLeft === 0 && step < 0) ||
        (direction && element.scrollLeft >= distance) ||
        (!direction && element.scrollLeft <= distance)
      if (endAnimation) {
        clearInterval(this.animationInterval)
        this.animationInterval = undefined
        element.scrollLeft = distance
      }
    }, interval)
  }

  updateDaySlotWidth = () => {
    this.setState({ daySlotWidth: this.getDaySlotWidth() })
  }

  getDaySlotWidth = () => {
    if (!this.daysHolder.current) {
      return
    }

    const daysHolderWidth = this.daysHolder.current.getBoundingClientRect().width
    return daysHolderWidth / 7
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDaySlotWidth)
    this.UNSAFE_componentWillReceiveProps(this.props)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDaySlotWidth)
  }

  UNSAFE_componentWillReceiveProps = (props: Props) => {
    const selectedDate = moment(props.date) || moment()

    this.setState(
      {
        loaded: false,
        date: selectedDate,
        events: props.events,
        days: this.fillDays(props.days),
        availableDays: props.availableDays ?? {},
      },
      () => {
        this.checkAvailableDays()
        const startOfSelectedWeek = moment(selectedDate).weekday(0)
        this.scrollTo(startOfSelectedWeek)
      }
    )
  }

  checkAvailableDays = () => {
    if (!this.state.loaded && this.state.events && this.state.events.length > 0) {
      const days = this.state.days as State['days']
      days.forEach(day => {
        day.empty = !(this.state.availableDays as State['availableDays'])[day.format('YYYY-MM-DD')]
      })
      this.setState({ days: days, loaded: true })
    }
  }

  fillDays = (length: number): ExtendedMoment[] => {
    if (this.state.days.length > 0) {
      return this.state.days
    }

    let daysToFill = Math.max(this.props.days, length)

    const specificStartDate = this.props.startDate || moment()
    const specificEndDate = this.props.startDate || moment()
    const startOfWeek = moment(specificStartDate).weekday(0)
    const addedDaysLength = moment(specificEndDate).diff(startOfWeek, 'days')
    if (addedDaysLength > 0) {
      daysToFill += 7
    }
    const days: ExtendedMoment[] = []
    for (let i = 0; i <= daysToFill; i += 1) {
      const day = moment(startOfWeek).add(i, 'days') as ExtendedMoment
      const isBefore = day.isBefore(momentTz(specificStartDate), 'day')
      const isAfter = day.isAfter(momentTz(specificEndDate).add(daysToFill, 'days'))
      day.isBeforeStart = isBefore
      day.isAfterEnd = isAfter
      day.empty = true
      days.push(day)
    }
    return days
  }

  onNext = (direction: -1 | 1) => {
    if (!this.daysHolder.current || !this.dayHolder.current) {
      return
    }
    const scrollLength =
      this.daysHolder.current.scrollLeft +
      this.dayHolder.current.getBoundingClientRect().width * 7 * direction
    this.animateScrollLeft(this.daysHolder.current, scrollLength, 100)
  }

  scrollTo = (selectedDate: Moment) => {
    if (this.dayHolder.current === null || this.daysHolder.current === null) {
      return
    }
    const specificStartDate = this.props.startDate || moment()
    const startOfWeek = specificStartDate.weekday(0)
    let dayNumber = selectedDate.diff(startOfWeek, 'days')
    if (dayNumber > 0) {
      dayNumber += 1
    }
    const scrollLength = this.dayHolder.current.getBoundingClientRect().width * (dayNumber - 1)
    this.animateScrollLeft(this.daysHolder.current, scrollLength, 100)
  }

  onDayClick = (date: ExtendedMoment) =>
    this.setState({ date }, () => {
      this.props.onDayChange(date)
    })

  onDayKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      focusFirstAvailableTimeSlot()
    }
  }

  renderArrow = (direction: -1 | 1, hideArrows: boolean) => {
    if (hideArrows) {
      return null
    }
    const iconName = direction < 0 ? 'fabe-icon-left' : 'fabe-icon-right'
    const holderName = direction < 0 ? 'fabe-left' : 'fabe-right'
    return (
      <Box w={['15%', 'auto']} mb={[4, 0]}>
        <div
          data-chili-css='date-arrows'
          data-test-id={`Days-arrow-${direction < 0 ? 'left' : 'right'}`}
          className={`fabe-arrow-holder ${holderName}`}
          onClick={() => {
            this.onNext(direction)
          }}
        >
          <div className={iconName} />
        </div>
      </Box>
    )
  }

  render() {
    return (
      <div className='fabe-days-chooser-holder'>
        {this.props.titleNode ? (
          <div className='fabe-prospect-header'>
            <Text as='h2' marginY={0}>
              <span data-chili-css='availability-header' className='fabe-prospect-name'>
                {this.props.titleNode}
              </span>
            </Text>
          </div>
        ) : null}
        {this.renderArrow(-1, this.props.hideArrows)}
        <div ref={this.daysHolder} className='fabe-days-chooser'>
          {this.state.days.map(day => {
            const width = this.state.daySlotWidth
              ? `${this.state.daySlotWidth}px`
              : this.getDaySlotWidth()
            const isSelected = this.state.loaded && this.state.date.isSame(day, 'day')
            return (
              <Day
                key={`day${day}`}
                ref={this.dayHolder}
                day={day}
                onClick={this.onDayClick}
                onKeyPress={this.onDayKeyPress}
                width={width}
                noMemberFound={this.props.noMemberFound}
                isSelected={isSelected}
              />
            )
          })}
        </div>
        {this.renderArrow(1, this.props.hideArrows)}
      </div>
    )
  }
}

export default Days

interface DayProps {
  day: ExtendedMoment
  isSelected: boolean
  noMemberFound: boolean
  onClick: (day: ExtendedMoment) => void
  onKeyPress?: React.KeyboardEventHandler<HTMLButtonElement>
  width?: string | number
}

const Day = forwardRef<HTMLDivElement, DayProps>(
  ({ day, onClick, width, isSelected, noMemberFound, onKeyPress }, ref) => {
    const locale = useLocale()

    const selected = isSelected ? 'fabe-date-selected fabe-non-clickable' : ''
    const disabled = day.isBeforeStart || day.isAfterEnd || day.empty || noMemberFound
    const greyed = disabled ? 'fabe-non-clickable fabe-date-greyed' : ''
    const allGreyed = noMemberFound ? 'fabe-all-greyed' : ''
    const dayLocale = moment(day).locale(locale)
    const month = dayLocale.format('MMM')
    const date = dayLocale.date()
    const dayNameFull = dayLocale.format('dddd')
    const dayNumber = dayLocale.format('Do')
    const monthFull = dayLocale.format('MMMM')
    const dateSlotFill = selected ? 'date-slot-fill' : 'date-slot-bar'

    return (
      <UnstyledButton
        disabled={disabled}
        onClick={() => {
          onClick(day)
        }}
        onKeyPress={onKeyPress}
      >
        <ScreenReaderOnly>
          {dayNameFull} {monthFull} {dayNumber} {selected ? 'is selected' : ''}
        </ScreenReaderOnly>
        <ScreenReaderOnly>Press enter to navigate available slots</ScreenReaderOnly>
        <div
          ref={ref}
          className={`fabe-date-slot ${selected} ${greyed} ${allGreyed}`}
          style={{ width: width, maxWidth: width, minWidth: width }}
        >
          <div data-chili-css='days-slot-bar' className='fabe-week-label' aria-hidden>
            <span>{dayLocale.format('ddd')}</span>
          </div>
          <div
            data-chili-css={dateSlotFill}
            className={`fabe-month-day-label ${selected ? 'fabe-month-day-label-selected' : ''}`}
            data-test-id={`days:${month}/${date}`}
          >
            <span data-chili-css='date-slot-fill-text' className='fabe-day' aria-hidden>
              {date}
            </span>
            <span data-chili-css='date-slot-fill-text' className='fabe-month' aria-hidden>
              {month}
            </span>
          </div>
        </div>
      </UnstyledButton>
    )
  }
)
Day.displayName = 'Day'
